// src/pages/MarketplaceIntegration/integrationService.js

// This service orchestrates all marketplace integration functionality
import marketplaceService from './marketplaceService';
import schedulerService from './schedulerService';
import notificationService from './notificationService';
import { db } from '../../firebase';
import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';

class IntegrationService {
  constructor() {
    this.syncInProgress = false;
    this.syncStats = {
      totalProducts: 0,
      updatedProducts: 0,
      failedProducts: 0,
      totalOrders: 0,
      importedOrders: 0,
      failedOrders: 0
    };
  }

  // Initialize the integration service
  async initialize(companyId) {
    this.companyId = companyId;
    console.log('Integration service initialized for company:', companyId);
    
    // Start the scheduler
    schedulerService.start();
    
    // Schedule periodic sync (every 30 minutes by default)
    schedulerService.scheduleJob(
      `marketplace-sync-${companyId}`, 
      30, 
      this.syncAllMarketplaces.bind(this),
      { companyId }
    );
  }

  // Get all marketplace connections for a company
  async getMarketplaceConnections() {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    try {
      const marketplacesRef = collection(db, 'companies', this.companyId, 'marketplaces');
      const snapshot = await getDocs(marketplacesRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching marketplace connections:', error);
      throw error;
    }
  }

  // Test marketplace connection
  async testMarketplaceConnection(marketplaceId) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    try {
      const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', marketplaceId);
      const marketplaceDoc = await getDoc(marketplaceRef);
      
      if (!marketplaceDoc.exists()) {
        throw new Error('Marketplace connection not found');
      }
      
      const marketplace = marketplaceDoc.data();
      
      // Test the connection
      const result = await marketplaceService.testConnection(
        marketplace.name,
        {
          apiKey: marketplace.apiKey,
          apiSecret: marketplace.apiSecret,
          supplierId: marketplace.supplierId // Pass supplier ID
        }
      );
      
      // Update last test timestamp
      await updateDoc(marketplaceRef, {
        lastTest: new Date(),
        lastTestResult: result.success
      });
      
      return result;
    } catch (error) {
      console.error('Error testing marketplace connection:', error);
      
      // Update test result even on failure
      try {
        const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', marketplaceId);
        await updateDoc(marketplaceRef, {
          lastTest: new Date(),
          lastTestResult: false
        });
      } catch (updateError) {
        console.error('Error updating test result:', updateError);
      }
      
      throw error;
    }
  }

  // Sync a single marketplace
  async syncMarketplace(marketplaceId) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    try {
      // Get marketplace connection details
      const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', marketplaceId);
      const marketplaceDoc = await getDoc(marketplaceRef);
      
      if (!marketplaceDoc.exists()) {
        throw new Error('Marketplace connection not found');
      }
      
      const marketplace = marketplaceDoc.data();
      
      // Update sync status
      await updateDoc(marketplaceRef, {
        syncStatus: 'syncing',
        lastSyncAttempt: new Date()
      });
      
      // Reset sync stats
      this.syncStats = {
        totalProducts: 0,
        updatedProducts: 0,
        failedProducts: 0,
        totalOrders: 0,
        importedOrders: 0,
        failedOrders: 0
      };
      
      // Get marketplace products
      const products = await marketplaceService.getMarketplaceProducts(
        marketplace.name,
        {
          apiKey: marketplace.apiKey,
          apiSecret: marketplace.apiSecret,
          supplierId: marketplace.supplierId // Pass supplier ID
        }
      );
      
      this.syncStats.totalProducts = products.length;
      
      // Update products in accounting system
      for (const product of products) {
        try {
          await this.updateAccountingProduct(product, marketplaceId);
          this.syncStats.updatedProducts++;
        } catch (error) {
          console.error(`Error updating product ${product.sku}:`, error);
          this.syncStats.failedProducts++;
          
          // Log error
          await this.logError(marketplace.name, {
            type: 'product_sync',
            productSku: product.sku,
            message: error.message,
            timestamp: new Date()
          });
        }
      }
      
      // Get marketplace orders
      const orders = await marketplaceService.getMarketplaceOrders(
        marketplace.name,
        {
          apiKey: marketplace.apiKey,
          apiSecret: marketplace.apiSecret,
          supplierId: marketplace.supplierId // Pass supplier ID
        }
      );
      
      this.syncStats.totalOrders = orders.length;
      
      // Import orders to accounting system
      for (const order of orders) {
        try {
          await this.importOrder(order, marketplace.name);
          this.syncStats.importedOrders++;
          
          // Send notification for new orders
          notificationService.sendNewOrderNotification(marketplace.name, order);
        } catch (error) {
          console.error(`Error importing order ${order.orderNumber}:`, error);
          this.syncStats.failedOrders++;
          
          // Log error
          await this.logError(marketplace.name, {
            type: 'order_import',
            orderNumber: order.orderNumber,
            message: error.message,
            timestamp: new Date()
          });
        }
      }
      
      // Update sync status to success
      await updateDoc(marketplaceRef, {
        syncStatus: 'success',
        lastSync: new Date(),
        lastSyncStats: this.syncStats
      });
      
      // Send sync complete notification
      notificationService.sendSyncCompleteNotification(marketplace.name, this.syncStats);
      
      return {
        success: true,
        stats: this.syncStats
      };
    } catch (error) {
      console.error('Error syncing marketplace:', error);
      
      // Update sync status to error
      try {
        const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', marketplaceId);
        await updateDoc(marketplaceRef, {
          syncStatus: 'error',
          lastSyncError: error.message,
          lastSyncAttempt: new Date()
        });
      } catch (updateError) {
        console.error('Error updating sync status:', updateError);
      }
      
      // Log error
      try {
        await this.logError('system', {
          type: 'sync_error',
          marketplaceId,
          message: error.message,
          timestamp: new Date()
        });
      } catch (logError) {
        console.error('Error logging sync error:', logError);
      }
      
      throw error;
    }
  }

  // Sync all marketplaces
  async syncAllMarketplaces() {
    if (this.syncInProgress) {
      console.log('Sync already in progress, skipping');
      return;
    }
    
    this.syncInProgress = true;
    
    try {
      console.log('Starting sync for all marketplaces');
      
      const marketplaces = await this.getMarketplaceConnections();
      const activeMarketplaces = marketplaces.filter(m => m.isActive);
      
      // Sync each marketplace
      for (const marketplace of activeMarketplaces) {
        try {
          await this.syncMarketplace(marketplace.id);
        } catch (error) {
          console.error(`Error syncing marketplace ${marketplace.name}:`, error);
          // Continue with other marketplaces
        }
      }
      
      console.log('Completed sync for all marketplaces');
    } catch (error) {
      console.error('Error in syncAllMarketplaces:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  // Get marketplace products
  async getMarketplaceProducts(marketplaceId) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    try {
      // Get marketplace connection details
      const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', marketplaceId);
      const marketplaceDoc = await getDoc(marketplaceRef);
      
      if (!marketplaceDoc.exists()) {
        throw new Error('Marketplace connection not found');
      }
      
      const marketplace = marketplaceDoc.data();
      console.log('Getting products for marketplace:', marketplace);
      
      // Get marketplace products
      const products = await marketplaceService.getMarketplaceProducts(
        marketplace.name,
        {
          apiKey: marketplace.apiKey,
          apiSecret: marketplace.apiSecret,
          supplierId: marketplace.supplierId // Pass supplier ID
        }
      );
      
      console.log('Retrieved products:', products);
      return products;
    } catch (error) {
      console.error('Error fetching marketplace products:', error);
      throw error;
    }
  }

  // Get marketplace orders
  async getMarketplaceOrders(marketplaceId) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    try {
      // Get marketplace connection details
      const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', marketplaceId);
      const marketplaceDoc = await getDoc(marketplaceRef);
      
      if (!marketplaceDoc.exists()) {
        throw new Error('Marketplace connection not found');
      }
      
      const marketplace = marketplaceDoc.data();
      
      // Get orders from marketplace API
      const orders = await marketplaceService.getMarketplaceOrders(
        marketplace.name,
        {
          apiKey: marketplace.apiKey,
          apiSecret: marketplace.apiSecret,
          supplierId: marketplace.supplierId // Pass supplier ID
        }
      );
      
      return orders;
    } catch (error) {
      console.error('Error fetching marketplace orders:', error);
      // Log the error
      await this.logError(marketplaceId, {
        type: 'order_import',
        message: `Error fetching orders: ${error.message}`,
        timestamp: new Date()
      });
      throw error;
    }
  }

  // Update accounting product based on marketplace data
  async updateAccountingProduct(marketplaceProduct, marketplaceId) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }
    
    try {
      // Get product mapping
      const mappingRef = doc(db, 'companies', this.companyId, 'marketplaceMappings', `${marketplaceId}_${marketplaceProduct.sku}`);
      const mappingDoc = await getDoc(mappingRef);
      
      if (!mappingDoc.exists()) {
        // No mapping found, skip this product
        console.log(`No mapping found for marketplace product ${marketplaceProduct.sku}`);
        return;
      }
      
      const mapping = mappingDoc.data();
      const accountingProductId = mapping.accountingProductId;
      
      // Get accounting product
      const productRef = doc(db, 'companies', this.companyId, 'products', accountingProductId);
      const productDoc = await getDoc(productRef);
      
      if (!productDoc.exists()) {
        throw new Error(`Accounting product ${accountingProductId} not found`);
      }
      
      const product = productDoc.data();
      
      // Update stock if different
      if (product.quantity !== marketplaceProduct.stock) {
        await updateDoc(productRef, {
          quantity: marketplaceProduct.stock,
          lastStockSync: new Date(),
          lastStockSyncSource: marketplaceId
        });
        
        // Add stock movement record
        const movementRef = doc(collection(productRef, 'movements'));
        await setDoc(movementRef, {
          date: new Date(),
          type: 'Pazaryeri Stok Senkronizasyonu',
          quantity: `${marketplaceProduct.stock} ${product.unit || 'Adet'}`,
          source: marketplaceId
        });
        
        console.log(`Updated stock for product ${product.name} from ${product.quantity} to ${marketplaceProduct.stock}`);
      }
    } catch (error) {
      console.error('Error updating accounting product:', error);
      throw error;
    }
  }

  // Import order from marketplace to accounting system
  async importOrder(marketplaceOrder, marketplaceName) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }
    
    try {
      // Check if order already exists
      const ordersRef = collection(db, 'companies', this.companyId, 'orders');
      const q = query(ordersRef, where('marketplaceOrderId', '==', marketplaceOrder.id));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Order already exists, skip
        console.log(`Order ${marketplaceOrder.id} already exists, skipping`);
        return;
      }
      
      // Create new order
      const newOrderRef = doc(ordersRef);
      
      // Prepare order data
      const orderData = {
        marketplaceOrderId: marketplaceOrder.id,
        marketplaceName: marketplaceName,
        customerName: marketplaceOrder.customer,
        orderDate: new Date(),
        lineItems: marketplaceOrder.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price,
          total: item.quantity * item.price
        })),
        total: marketplaceOrder.total,
        status: 'Sipariş Alındı',
        currency: 'TRY', // Default currency
        createdAt: new Date()
      };
      
      // Calculate subtotals
      const subTotal = orderData.lineItems.reduce((sum, item) => sum + item.total, 0);
      const vatRate = 0.18; // Default VAT rate
      const vatAmount = subTotal * vatRate;
      const total = subTotal + vatAmount;
      
      orderData.subTotal = subTotal;
      orderData.vatRate = vatRate;
      orderData.vatAmount = vatAmount;
      orderData.total = total;
      
      // Save order
      await setDoc(newOrderRef, orderData);
      
      console.log(`Imported order ${marketplaceOrder.id} from ${marketplaceName}`);
    } catch (error) {
      console.error('Error importing order:', error);
      throw error;
    }
  }

  // Update marketplace stock when accounting product stock changes
  async updateMarketplaceStock(accountingProductId, newStock) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }
    
    try {
      // Get all marketplace mappings for this product
      const mappingsRef = collection(db, 'companies', this.companyId, 'marketplaceMappings');
      const q = query(mappingsRef, where('accountingProductId', '==', accountingProductId));
      const snapshot = await getDocs(q);
      
      const mappings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Update stock on each marketplace
      for (const mapping of mappings) {
        try {
          // Get marketplace connection
          const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', mapping.marketplaceId);
          const marketplaceDoc = await getDoc(marketplaceRef);
          
          if (!marketplaceDoc.exists()) continue;
          
          const marketplace = marketplaceDoc.data();
          
          if (!marketplace.isActive) continue;
          
          // Update stock on marketplace
          const result = await marketplaceService.updateMarketplaceStock(
            marketplace.name,
            {
              apiKey: marketplace.apiKey,
              apiSecret: marketplace.apiSecret,
              supplierId: marketplace.supplierId // Pass supplier ID
            },
            {
              sku: mapping.marketplaceSku,
              stock: newStock
            }
          );
          
          console.log(`Updated stock for ${mapping.marketplaceSku} on ${marketplace.name}:`, result);
          
          // Send notification
          notificationService.sendStockSyncNotification(
            marketplace.name,
            { name: mapping.marketplaceSku, stock: newStock },
            'success'
          );
        } catch (error) {
          console.error(`Error updating stock on marketplace:`, error);
          
          // Log error
          await this.logError('stock_sync', {
            type: 'stock_sync_error',
            productId: accountingProductId,
            message: error.message,
            timestamp: new Date()
          });
          
          // Send error notification
          notificationService.sendStockSyncNotification(
            'unknown',
            { name: accountingProductId, stock: newStock },
            'error'
          );
        }
      }
    } catch (error) {
      console.error('Error updating marketplace stock:', error);
      throw error;
    }
  }

  // Log error to database
  async logError(marketplace, errorData) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }
    
    try {
      const errorsRef = collection(db, 'companies', this.companyId, 'marketplaceErrors');
      const errorRef = doc(errorsRef);
      
      await setDoc(errorRef, {
        marketplace,
        ...errorData,
        companyId: this.companyId,
        createdAt: new Date()
      });
      
      console.log('Error logged:', errorData);
    } catch (error) {
      console.error('Error logging error to database:', error);
    }
  }

  // Get error logs
  async getErrorLogs(filters = {}) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }
    
    try {
      const errorsRef = collection(db, 'companies', this.companyId, 'marketplaceErrors');
      const snapshot = await getDocs(errorsRef);
      
      let errors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply filters
      if (filters.marketplace) {
        errors = errors.filter(e => e.marketplace === filters.marketplace);
      }
      
      if (filters.type) {
        errors = errors.filter(e => e.type === filters.type);
      }
      
      if (filters.startDate) {
        errors = errors.filter(e => e.createdAt.toDate() >= filters.startDate);
      }
      
      if (filters.endDate) {
        errors = errors.filter(e => e.createdAt.toDate() <= filters.endDate);
      }
      
      // Sort by date (newest first)
      errors.sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
      
      return errors;
    } catch (error) {
      console.error('Error fetching error logs:', error);
      throw error;
    }
  }

  // Add new marketplace connection
  async addMarketplace(marketplaceData) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    try {
      const marketplacesRef = collection(db, 'companies', this.companyId, 'marketplaces');
      const newMarketplaceRef = doc(marketplacesRef);
      await setDoc(newMarketplaceRef, {
        ...marketplaceData,
        createdAt: new Date(),
        isActive: true,
        lastSync: null,
        syncStatus: 'idle'
      });
      
      return newMarketplaceRef.id;
    } catch (error) {
      console.error('Error adding marketplace:', error);
      throw error;
    }
  }

  // Update marketplace connection
  async updateMarketplace(id, marketplaceData) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    try {
      const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', id);
      await updateDoc(marketplaceRef, marketplaceData);
    } catch (error) {
      console.error('Error updating marketplace:', error);
      throw error;
    }
  }

  // Delete marketplace connection
  async deleteMarketplace(id) {
    if (!this.companyId) {
      throw new Error('Service not initialized. Call initialize() first.');
    }

    try {
      const marketplaceRef = doc(db, 'companies', this.companyId, 'marketplaces', id);
      await deleteDoc(marketplaceRef);
    } catch (error) {
      console.error('Error deleting marketplace:', error);
      throw error;
    }
  }

  // Clean up resources
  cleanup() {
    schedulerService.stop();
    console.log('Integration service cleaned up');
  }
}

// Export singleton instance
const integrationService = new IntegrationService();
export default integrationService;